/**
 * Utilitário para encriptar/descriptografar o token de acesso antes de
 * persistí-lo no `localStorage`.
 *
 * Implementação usa AES-GCM (autenticado) via Web Crypto API nativa.
 * A chave é derivada de uma passphrase fixa via PBKDF2.
 *
 * Importante: como a passphrase está no bundle JS, um atacante determinado
 * pode extraí-la. Esta camada protege contra inspeção casual de localStorage,
 * extensões maliciosas básicas e XSS persistentes simples - mas não substitui
 * cookies HttpOnly. Use HTTPS sempre.
 */

const PASSPHRASE = 'urbnet-ouvidoria::token-cipher::v1';
const SALT = new TextEncoder().encode('urbnet-ouvidoria::salt::v1');
const PBKDF2_ITERATIONS = 100_000;
const IV_LENGTH = 12; // bytes (recomendado para AES-GCM)

let cachedKey: CryptoKey | null = null;

async function getKey(): Promise<CryptoKey> {
  if (cachedKey) return cachedKey;

  const passphraseKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(PASSPHRASE),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  cachedKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: SALT,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    passphraseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  return cachedKey;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Encripta um token usando AES-GCM-256.
 * Saída: base64 de `IV (12 bytes) || ciphertext+tag`.
 *
 * @param token Token em texto plano.
 * @returns Promise com a string base64 contendo o token encriptado.
 */
export async function encryptToken(token: string): Promise<string> {
  if (!token) return '';

  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const data = new TextEncoder().encode(token);

  const cipherBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    data
  );
  const cipherBytes = new Uint8Array(cipherBuffer);

  const combined = new Uint8Array(iv.length + cipherBytes.length);
  combined.set(iv, 0);
  combined.set(cipherBytes, iv.length);

  return bytesToBase64(combined);
}

/**
 * Descriptografa um token previamente encriptado por `encryptToken`.
 *
 * @param encrypted String base64 produzida por `encryptToken`.
 * @returns Promise com o token em texto plano, ou string vazia em caso de falha.
 */
export async function decryptToken(encrypted: string): Promise<string> {
  if (!encrypted) return '';

  try {
    const combined = base64ToBytes(encrypted);
    if (combined.length <= IV_LENGTH) return '';

    const iv = combined.slice(0, IV_LENGTH);
    const cipherBytes = combined.slice(IV_LENGTH);

    const key = await getKey();
    const plainBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      cipherBytes
    );

    return new TextDecoder().decode(plainBuffer);
  } catch {
    return '';
  }
}

/**
 * Encripta um ID (municipioId, ouvidoriaId, agenteId) usando AES-GCM-256.
 * Usa a mesma lógica de encriptação de tokens.
 *
 * @param id ID em texto plano.
 * @returns Promise com a string base64 contendo o ID encriptado.
 */
export async function encryptId(id: string): Promise<string> {
  return encryptToken(id);
}

/**
 * Descriptografa um ID previamente encriptado por `encryptId`.
 * Usa a mesma lógica de descriptografia de tokens.
 *
 * @param encrypted String base64 produzida por `encryptId`.
 * @returns Promise com o ID em texto plano, ou string vazia em caso de falha.
 */
export async function decryptId(encrypted: string): Promise<string> {
  return decryptToken(encrypted);
}
